import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  criarCard,
  ligarCardNoCurso,
  mensagemDoCatalogo,
  porCardNaSecao,
  removerSecao,
  salvarSecao,
  type Resposta,
} from "../lib/adminCatalogo";
import type { PropsDoCatalogo } from "./AdminCatalogo";
import { CAMPO, CampoDeBusca, Recado } from "./ui";
import { ehFerramentaDeCurso } from "./AdminCatalogoUi";
import { useRolarAoEscolher } from "../lib/rolarPara";
import { slugDeTitulo } from "../lib/slug";
import AdminCatalogoFerramenta from "./AdminCatalogoFerramenta";

// Admin › Catálogo › **Ferramentas** — o que cada curso MOSTRA ao aluno
// (000256). O rótulo da sub-aba virou "Ferramentas" em 21/09, a pedido de
// Victor; o endereço continua `/admin/catalogo/vitrine` e o arquivo também,
// porque VITRINE é a palavra do domínio (`vitrine_secoes`,
// `vw_vitrine_secoes`, `vw_minha_vitrine`) e renomear a tela por causa do
// rótulo faria o código discordar do banco.
//
// ---------------------------------------------------------------------------
// A sub-aba em que um clique tira ferramenta de quem pagou
// ---------------------------------------------------------------------------
// Desligar um par (produto, card) não devolve erro nem deixa rastro na tela do
// aluno: a ferramenta simplesmente deixa de estar lá. Por isso nada aqui é
// decidido no front — o motivo obrigatório, a contagem de quem perde o quê e as
// recusas são das RPCs, e esta tela mostra a frase que elas mandam. Uma segunda
// tabela de frases escrita em TypeScript divergiria da do banco em silêncio, e a
// que divergisse seria justamente a que ninguém lê.
//
// ---------------------------------------------------------------------------
// Uma linha por CARD, um botão por CURSO
// ---------------------------------------------------------------------------
// A forma óbvia seria "por produto, os ligados e os disponíveis". Ela conta a
// mesma coisa duas vezes na Escola, que tem dois SKUs e um curso só: são 33
// cards, então as duas listas de dois produtos põem ~66 linhas na mesma tela, e
// o card aparece em quatro lugares. A régua da casa manda contar quantas vezes a
// mesma informação aparece antes de dar a tela por pronta.
//
// Até 21/09 o card era a LINHA e cada produto do curso era um BOTÃO dela — a
// Escola com dois. Victor mandou deixar só um: *"todas as ferramentas do
// vitalício são as mesmas do escola pro"*, e a medição concorda (os dois SKUs
// têm os mesmos 10 cards, zero de diferença).
//
// O que mudou não foi esconder o segundo botão, e sim o ATO virar do curso
// (000269): um clique escreve nos dois SKUs, numa transação. Esconder deixaria
// a Vitalícia para trás na primeira ferramenta nova, que é justamente a
// assimetria que a versão anterior existia para mostrar. Quando ela já existe
// no banco — e ela pode, de antes desta tela —, o botão DIZ "está em 1 de 2" em
// vez de arredondar para ligado ou desligado: os dois arredondamentos mentem.
//
// ---------------------------------------------------------------------------
// O aviso que a tela existe para dar: card ligado que o aluno NÃO vê
// ---------------------------------------------------------------------------
// Num curso COM seções, `Curso.tsx` desenha só o que está dentro delas
// (`secoes.map(...)`, nunca a lista completa). Então um card ligado ao produto e
// fora de toda seção some da tela — sem erro, sem log, e quem nota é o aluno. O
// teste 24 prova isso do lado do banco desde a 000041; aqui ele vira um número
// na tela de quem mexe.
//
// A conta inclui os UNIVERSAIS de propósito, e isso é mais largo que o par: eles
// não passam por `produto_cards`, mas `cardsDoCurso` os põe na mesma lista que o
// ramo das seções filtra, então um universal fora de seção some igual. O que
// fica de fora são os de escopo `conta` (a tela do curso nunca os desenha) e os
// desativados (esses já não aparecem por outro motivo, e acusá-los seria alarme
// falso).
//
// É derivação de APRESENTAÇÃO sobre dado que já veio na mesma ida — não é regra
// de acesso, e por isso pode morar no front.
//
// ---------------------------------------------------------------------------
// UM formulário aberto por vez, e isso é decisão
// ---------------------------------------------------------------------------
// Os quatro atos com campo de motivo (`editar card`, `seção`, `desligar`,
// `apagar seção`) moram num estado só. Duas razões, e as duas são cicatriz:
//
//   · **para quem opera** — cada um destes atos alcança alunos, e dois abertos
//     ao mesmo tempo é o convite a confirmar o errado;
//   · **para quem prova** — com dois abertos haveria dois `data-campo="motivo"`
//     e dois `data-confirmar-vitrine` no DOM, e um seletor por atributo pegaria
//     o primeiro. É a cicatriz dos sete rótulos iguais da calculadora, em que a
//     prova escrevia num checkbox e respondia "ok".

/** O único ato aberto na tela. A união é o que garante "um por vez". */
type Edicao =
  | { tipo: "secao"; id: string | null; titulo: string; ordem: string; motivo: string }
  // `ligar` diz a DIREÇÃO do ato. Até 22/09 só o desligar passava por aqui —
  // ligar agia no clique, com o argumento de que "ligar não tira nada de
  // ninguém". Victor pediu a mesma confirmação nos dois, e a razão dele vence a
  // minha: ligar uma ferramenta num curso a põe na tela de milhares de alunos
  // de uma vez, e isso é tão irreversível na prática quanto tirar — quem viu,
  // viu. As duas direções agora pedem justificativa, e ela vai ao razão.
  | { tipo: "ligar-desligar"; ligar: boolean; produto: string; card: string; motivo: string }
  | { tipo: "remover-secao"; id: string; titulo: string; motivo: string }
  // A ferramenta NOVA (000299). `slugTocado` separa o identificador SUGERIDO
  // do escrito: enquanto ninguém mexe nele, ele acompanha o nome; depois de
  // mexido, é da pessoa, e a tela para de reescrevê-lo por baixo dela.
  | {
      tipo: "nova-ferramenta";
      nome: string;
      slug: string;
      slugTocado: boolean;
      descricao: string;
      sobre: string;
      motivo: string;
    };

/** Busca local sem acento — o catálogo inteiro já está em memória. */
function normalizar(t: string): string {
  return t
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export default function AdminCatalogoVitrine({ catalogo, recarregar }: PropsDoCatalogo) {
  const [params, setParams] = useSearchParams();
  const curso = params.get("curso") ?? "";
  // O slug da ferramenta ABERTA. Presente = a tela dedicada; ausente = a
  // vitrine do curso.
  const ferramenta = params.get("card") ?? "";
  const irPara = (proximo: { curso?: string; card?: string }) => {
    const p = new URLSearchParams(params);
    for (const [chave, valor] of Object.entries(proximo)) {
      if (valor) p.set(chave, valor);
      else p.delete(chave);
    }
    setParams(p, { replace: false });
  };
  const [busca, setBusca] = useState("");
  // O passo da casa (o balcão, a busca de CNAE, as disciplinas). São 33 cards
  // hoje, então o corte quase nunca age — ele existe para o dia em que o
  // catálogo crescer, e não para o de hoje.
  const [mostrando, setMostrando] = useState(25);
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  // A falha carrega ONDE ela aconteceu. Sem leitura não há tela; sem UMA
  // escrita há tela e falta um tique — então o erro de um ato fica na linha
  // dele, nunca no lugar da tela inteira.
  const [falha, setFalha] = useState<{ onde: string; texto: string } | null>(null);
  // Qual card o operador escolheu pôr em cada seção. É por seção porque as seis
  // aparecem juntas, e um seletor só faria a escolha saltar de linha.
  const [aPor, setAPor] = useState<Record<string, string>>({});
  // A frase do banco sobre a ferramenta que ACABOU de nascer. A tela vai para a
  // ficha dela, e a frase vai junto — é ela que diz o próximo passo (ligar num
  // curso) e por que a ferramenta ainda não abre.
  const [criada, setCriada] = useState<{ slug: string; texto: string } | null>(null);

  // Os dois formulários que moram ACIMA das listas. Quem clica em "Editar" numa
  // linha do fim não vê nada acontecer — a tela responde fora do campo de
  // visão, e a pessoa conclui que o clique não pegou (o mesmo defeito de 28/08
  // em três telas). `useRolarAoEscolher` desconta o Topo por `--cf-topo`,
  // respeita `prefers-reduced-motion` e não mexe no que já está visível.
  const caixaDaSecao = useRef<HTMLElement | null>(null);
  const caixaDaNova = useRef<HTMLElement | null>(null);

  const formSecao = edicao?.tipo === "secao" ? edicao : null;
  /** O par (produto, card) com a confirmação aberta — para ligar OU desligar. */
  const confirmandoPar = edicao?.tipo === "ligar-desligar" ? edicao : null;
  const removendo = edicao?.tipo === "remover-secao" ? edicao : null;
  const novaFerramenta = edicao?.tipo === "nova-ferramenta" ? edicao : null;

  useRolarAoEscolher(caixaDaSecao, formSecao ? (formSecao.id ?? "nova") : null);
  useRolarAoEscolher(caixaDaNova, novaFerramenta ? "nova-ferramenta" : null);

  const cursoAtual = useMemo(
    () => catalogo.cursos.find((c) => c.slug === curso) ?? null,
    [catalogo.cursos, curso],
  );

  // Os produtos do curso. A Escola tem dois (Pro e Vitalícia) e um curso só —
  // é assim que a Guru vende, e é por isso que o par é (produto, card) e não
  // (curso, card).
  const produtosDoCurso = useMemo(
    () => catalogo.produtos.filter((p) => p.curso_slug === curso),
    [catalogo.produtos, curso],
  );

  /**
   * Os cards que NÃO têm par possível, e a razão de cada um:
   *
   *   · **universal** — a vitrine o mostra em todo curso, sem passar por
   *     `produto_cards`;
   *   · **escopo `conta`** — ele aparece uma vez na tela inicial e não pertence
   *     a curso nenhum (é o Contrate um Contador).
   *
   * Eles aparecem numa lista à parte e SEM botão de ligar: o banco recusa os
   * dois, e oferecer o botão seria prometer um ato que não existe.
   */
  const semPar = useMemo(
    () => catalogo.cards.filter((c) => c.universal || c.escopo === "conta"),
    [catalogo.cards],
  );

  /*
   * Os MÓDULOS DA FACULDADE — hoje `minhas-notas` e `secretaria-do-aluno` —
   * NÃO aparecem nesta tela, e a ausência é decisão de Victor (21/09):
   * *"esses módulos não precisam ser citados. Eles são fixos para todas as Pós
   * e MBA"*.
   *
   * Eles saíram da lista de cards logo abaixo (`doPar` filtra
   * `tipo !== 'modulo'`), e por algumas horas tiveram um bloco próprio, que
   * também saiu: dizer duas vezes por curso uma coisa que vale igual para os
   * nove é a cicatriz do "conte quantas vezes a mesma informação aparece".
   *
   * Quem os separa é `cards.tipo = 'modulo'`, que é do BANCO — uma lista de
   * slugs escrita aqui divergiria no dia em que a FBC ganhasse o terceiro.
   */

  const porSlug = useMemo(
    () => new Map(catalogo.cards.map((c) => [c.slug, c])),
    [catalogo.cards],
  );

  /**
   * De qual CURSO é cada produto. A tela precisa disto na visão "todas", em que
   * a pergunta deixa de ser "este curso mostra?" e passa a ser "em quais cursos
   * isto está?".
   */
  const cursoDoProduto = useMemo(
    () => new Map(catalogo.produtos.map((p) => [p.slug, p.curso_slug])),
    [catalogo.produtos],
  );

  /**
   * Os cards que passam pelo par, ordenados: os que este curso mostra primeiro.
   *
   * **Sem curso escolhido, ela lista TODAS** (pedido de Victor, 22/09). Até
   * então a tela pedia o curso antes de desenhar, com o argumento de que "onze
   * cursos com trinta e três cards cada é um paredão" — e o argumento só vale
   * para a grade de BOTÕES, que é o que a visão geral não desenha: ali cada
   * linha diz em quais cursos a ferramenta está, e agir continua pedindo o
   * curso, porque o ato é dele.
   */
  const doPar = useMemo(() => {
    if (!cursoAtual) {
      return catalogo.cards
        .filter(ehFerramentaDeCurso)
        .map((c) => ({
          card: c,
          // Aqui `ligados` conta CURSOS distintos, não produtos: a Escola tem
          // dois SKUs com as mesmas ferramentas, e contar produtos diria "2"
          // para uma ferramenta que está em um curso só.
          ligados: new Set(
            c.produtos.map((x) => cursoDoProduto.get(x.slug)).filter(Boolean),
          ).size,
        }))
        .sort((a, b) => a.card.nome.localeCompare(b.card.nome, "pt-BR"));
    }
    const doCurso = new Set(produtosDoCurso.map((p) => p.slug));
    return catalogo.cards
      .filter(ehFerramentaDeCurso)
      .map((c) => ({ card: c, ligados: c.produtos.filter((x) => doCurso.has(x.slug)).length }))
      .sort(
        (a, b) =>
          b.ligados - a.ligados || a.card.nome.localeCompare(b.card.nome, "pt-BR"),
      );
  }, [catalogo.cards, catalogo.produtos, cursoAtual, produtosDoCurso, cursoDoProduto]);

  /** A lista que a tela DESENHA: a de cima, recortada pela busca. */
  const linhas = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return doPar;
    return doPar.filter((l) => normalizar(`${l.card.nome} ${l.card.slug}`).includes(termo));
  }, [doPar, busca]);

  // Os números do CURSO saem de `doPar`, nunca de `linhas`. As duas frases que
  // os usam falam do curso inteiro — "ligado(s) neste curso" e "os N ligados
  // hoje somem" —, e contá-los sobre o recorte da busca daria o número certo
  // para outra pergunta: quem procurasse "excel" antes de criar a primeira
  // seção leria que UM card some, quando somem todos. É a cicatriz dos combos
  // do balcão, em que a contagem respondia por um recorte que não era o da tela.
  const ligadosNoCurso = useMemo(() => doPar.filter((l) => l.ligados > 0).length, [doPar]);

  /**
   * Os cards que o curso mostra e que estão FORA de toda seção — ou seja, os que
   * o aluno não vê. Só existe em curso COM seções: sem elas o layout é o padrão
   * (portas + ferramentas) e todo card ligado aparece.
   *
   * Esta mesma lista é a do seletor "pôr nesta seção", logo abaixo: o aviso diz
   * quais somem e o editor oferece exatamente esses. Duas listas para a mesma
   * pergunta divergiriam na primeira mudança.
   */
  // Esta conta NÃO usa `ehFerramentaDeCurso`, e a diferença é o assunto: ali a
  // pergunta é "o curso entrega esta ferramenta?", e aqui é "o ALUNO vê este
  // card?". Um módulo da FBC ligado ao curso e fora de toda seção some da tela
  // dele igual a qualquer outro — filtrá-lo aqui esconderia o aviso.
  const orfaos = useMemo(() => {
    if (!cursoAtual || cursoAtual.secoes.length === 0) return [];
    const emSecao = new Set(cursoAtual.secoes.flatMap((s) => s.cards));
    const doCurso = new Set(produtosDoCurso.map((p) => p.slug));
    return catalogo.cards.filter(
      (c) =>
        c.ativo &&
        c.escopo !== "conta" &&
        (c.universal || c.produtos.some((x) => doCurso.has(x.slug))) &&
        !emSecao.has(c.slug),
    );
  }, [catalogo.cards, cursoAtual, produtosDoCurso]);

  function escolherCurso(slug: string) {
    irPara({ curso: slug, card: "" });
    setBusca("");
    setEdicao(null);
    setFalha(null);
    setRecado(null);
    // Trocar de curso volta ao topo da lista: continuar em "mostrando 75"
    // depois de trocar mostraria uma lista inteira que ninguém pediu.
    setMostrando(25);
  }

  /**
   * Todo ato passa por aqui, e a frase que aparece é sempre a do BANCO.
   *
   * `onde` é a âncora do erro. Numa recusa a tela NÃO fecha o formulário: a
   * recusa de "desligar sem motivo" já vem com quantos alunos perdem a
   * ferramenta, e fechar a caixa jogaria fora exatamente o número que o
   * operador precisa para escrever o porquê.
   */
  async function agir(onde: string, executar: () => Promise<Resposta>) {
    setSalvando(true);
    setFalha(null);
    setRecado(null);
    const r = await executar();
    setSalvando(false);
    if (!r.ok) {
      setFalha({ onde, texto: mensagemDoCatalogo(r) });
      return;
    }
    setRecado({ tipo: "ok", texto: mensagemDoCatalogo(r) });
    setEdicao(null);
    await recarregar();
  }

  /**
   * Cria a ferramenta e abre a FICHA dela (000299). O banco decide tudo — a
   * forma do identificador, a unicidade, que ela nasce em construção e sem
   * curso —, e a frase dele viaja até a ficha, onde o resto se preenche.
   */
  async function criarFerramenta() {
    if (!novaFerramenta) return;
    setSalvando(true);
    setFalha(null);
    setRecado(null);
    const r = await criarCard({
      slug: novaFerramenta.slug,
      nome: novaFerramenta.nome,
      descricao: novaFerramenta.descricao,
      sobre: novaFerramenta.sobre,
      motivo: novaFerramenta.motivo,
    });
    setSalvando(false);
    if (!r.ok) {
      setFalha({ onde: "nova-ferramenta", texto: mensagemDoCatalogo(r) });
      return;
    }
    const slug = typeof r.slug === "string" ? r.slug : novaFerramenta.slug;
    setEdicao(null);
    await recarregar();
    setCriada({ slug, texto: mensagemDoCatalogo(r) });
    irPara({ card: slug });
  }

  // A tela dedicada da ferramenta (000257). Vem DEPOIS de todos os hooks: um
  // `return` no meio deles derruba a rota com o erro React #310, que nem o
  // `tsc` nem a suíte SQL veem — o hub não tem eslint.
  if (ferramenta) {
    return (
      <AdminCatalogoFerramenta
        catalogo={catalogo}
        recarregar={recarregar}
        slug={ferramenta}
        voltar={curso ? `?curso=${encodeURIComponent(curso)}` : "?"}
        aviso={criada?.slug === ferramenta ? criada.texto : null}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* ── A barra ────────────────────────────────────────────────────────── */}
      <div className="surface rise p-5 sm:p-6">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex min-w-56 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Curso</span>
            <select
              data-filtro="curso"
              className={CAMPO}
              value={curso}
              onChange={(e) => escolherCurso(e.target.value)}
            >
              {/* "Todas" é o padrão desde 22/09 (pedido de Victor). Ela mostra
                  a lista inteira em modo de LEITURA — em quais cursos cada
                  ferramenta está —, e não a grade de botões: um botão por curso
                  em cada linha seria o paredão que o comentário antigo temia, e
                  o ato continua sendo do curso. */}
              <option value="">Todas as ferramentas</option>
              {catalogo.cursos.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.nome || c.slug}
                </option>
              ))}
            </select>
          </label>

          {/* A busca vale nos DOIS modos: a lista de todas tem 26 linhas, e
              procurar pelo nome é o gesto principal dela. */}
          <div className="min-w-64 flex-1">
            <CampoDeBusca
              valor={busca}
              aoMudar={setBusca}
              rotulo="Buscar card"
              placeholder="Nome ou slug do card"
              aoLimpar={busca ? () => setBusca("") : undefined}
            />
          </div>

          {/* A ferramenta NOVA (000299). O botão fica para todo mundo que VÊ o
              Catálogo, como os outros desta aba: quem decide se a pessoa edita
              é o banco, e a recusa dele diz como pedir a permissão. */}
          <button
            type="button"
            data-nova-ferramenta
            onClick={() => {
              setFalha(null);
              setRecado(null);
              setEdicao({
                tipo: "nova-ferramenta",
                nome: "",
                slug: "",
                slugTocado: false,
                descricao: "",
                sobre: "",
                motivo: "",
              });
            }}
            className="btn-contorno min-h-11 px-5 text-sm"
          >
            Nova ferramenta
          </button>
        </div>

        {cursoAtual && (
          <p className="mt-4 text-sm text-cinza">
            {ligadosNoCurso} de {doPar.length} card(s) ligado(s) neste curso ·{" "}
            {produtosDoCurso.length} produto(s):{" "}
            {produtosDoCurso.map((p) => p.slug).join(", ") || "nenhum"} ·{" "}
            {cursoAtual.secoes.length > 0
              ? `${cursoAtual.secoes.length} seção(ões)`
              : "sem seções — layout padrão"}
          </p>
        )}
      </div>

      {/* ── A ferramenta nova (000299) ───────────────────────────────────── */}
      {novaFerramenta && (
        <section
          ref={caixaDaNova}
          role="group"
          aria-labelledby="titulo-nova-ferramenta"
          className="surface rise p-6"
          data-form-nova-ferramenta
        >
          <h3 id="titulo-nova-ferramenta" className="font-display text-lg font-bold text-titulo">
            Nova ferramenta
          </h3>
          {/* O que ACONTECE é dito antes do clique, e não descoberto depois: a
              ferramenta nasce em construção e sem curso, então ninguém a vê. É
              a família do "Limite com a prorrogação" — o que a tela não diz, o
              olho preenche com a suposição errada ("criei, e os alunos já
              veem"). */}
          <p className="mt-1 text-sm text-cinza">
            Ela nasce <strong className="text-tinta">em construção e sem curso</strong>: ninguém a
            vê até alguém ligá-la num curso, aqui mesmo nesta lista. Para ela <em>abrir</em>, o
            destino precisa de código — uma rota no hub ou uma credencial —, e isso entra com o
            porte dela.
          </p>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Nome</span>
              <input
                data-campo="nome-nova"
                className={CAMPO}
                value={novaFerramenta.nome}
                maxLength={80}
                onChange={(e) => {
                  const nome = e.target.value;
                  setEdicao({
                    ...novaFerramenta,
                    nome,
                    // Enquanto ninguém mexeu no identificador, ele acompanha o
                    // nome — pela MESMA derivação do Calendário (`slugDeTitulo`).
                    // É sugestão: quem confere a forma é o banco.
                    slug: novaFerramenta.slugTocado ? novaFerramenta.slug : slugDeTitulo(nome),
                  });
                }}
                placeholder="É o que o aluno lê no card"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold text-tinta">Identificador</span>
              <input
                data-campo="slug-nova"
                className={`${CAMPO} font-mono`}
                value={novaFerramenta.slug}
                maxLength={60}
                onChange={(e) =>
                  setEdicao({ ...novaFerramenta, slug: e.target.value, slugTocado: true })
                }
                placeholder="radar-simples"
              />
              <span className="text-xs text-cinza">
                Minúsculas sem acento, números e hífen. Ele vira o endereço da ferramenta e{" "}
                <strong>não muda depois</strong>.
              </span>
            </label>

            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Descrição</span>
              <input
                data-campo="descricao-nova"
                className={CAMPO}
                value={novaFerramenta.descricao}
                onChange={(e) => setEdicao({ ...novaFerramenta, descricao: e.target.value })}
                placeholder="A linha embaixo do nome, na tela do curso — o aluno lê"
              />
            </label>

            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">O que é esta ferramenta</span>
              <textarea
                data-campo="sobre-nova"
                rows={4}
                className={`${CAMPO} min-h-24 resize-y`}
                value={novaFerramenta.sobre}
                onChange={(e) => setEdicao({ ...novaFerramenta, sobre: e.target.value })}
                placeholder="Para quem opera — nenhum aluno vê. O que ela faz, para quem serve e o que não faz."
              />
            </label>

            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-semibold text-tinta">Por que ela entra</span>
              <input
                data-campo="motivo-nova"
                className={CAMPO}
                value={novaFerramenta.motivo}
                onChange={(e) => setEdicao({ ...novaFerramenta, motivo: e.target.value })}
                placeholder="Vai para o histórico da ferramenta, na criação"
              />
            </label>
          </div>

          {falha?.onde === "nova-ferramenta" && <Recado tipo="erro" texto={falha.texto} />}

          <div className="mt-5 flex flex-wrap gap-3">
            {/* Sem `disabled` por campo vazio: quem recusa é o banco, com a
                frase que diz o que falta. */}
            <button
              type="button"
              data-criar-ferramenta
              disabled={salvando}
              onClick={() => void criarFerramenta()}
              className="pill-cta min-h-12 px-6 disabled:opacity-55"
            >
              {salvando ? "Criando…" : "Criar ferramenta"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEdicao(null);
                setFalha(null);
              }}
              className="btn-contorno min-h-12 text-sm"
            >
              Cancelar
            </button>
          </div>
        </section>
      )}

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {/* ── O aviso que esta tela existe para dar ──────────────────────────── */}
      {orfaos.length > 0 && (
        <section
          role="group"
          aria-labelledby="titulo-orfaos"
          className="rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-5"
        >
          <h3 id="titulo-orfaos" className="font-display text-base font-bold text-alerta-tinta">
            {orfaos.length} card(s) que este curso liga e o aluno NÃO vê
          </h3>
          <p className="mt-1 text-sm text-alerta-tinta">
            Este curso tem seções, e a tela do curso desenha só o que está dentro
            delas. Card fora de toda seção some da tela — sem erro e sem aviso,
            então quem nota é o aluno. Ponha cada um numa seção, abaixo.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {orfaos.map((c) => (
              <li
                key={c.slug}
                data-card-orfao={c.slug}
                className="rounded-full bg-superficie px-3 py-1 text-sm text-tinta"
              >
                {c.nome}
                {c.universal && <span className="ml-1 text-cinza">(universal)</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        NÃO há formulário de card aqui, e a ausência é decisão de 16/09: editar
        a ferramenta é numa TELA DEDICADA (`?card=<slug>`), a pedido de Victor.

        O que importa é a remoção, não a tela nova. Duas telas editando o mesmo
        card seriam duas portas para a mesma ação, e a segunda porta é a que
        fica desatualizada — é a razão de este painel não ter uma aba "Acessos"
        ao lado da de Alunos.
      */}

      {/* ── Os cards do curso ──────────────────────────────────────────────── */}
      <section className="surface rise overflow-hidden" role="group" aria-labelledby="titulo-pares">
        <div className="border-b border-borda p-5">
          <h3 id="titulo-pares" className="font-display text-base font-bold text-titulo">
            {cursoAtual ? "O que este curso mostra" : "Todas as ferramentas"}
          </h3>
          <p className="mt-1 text-sm text-cinza">
            {cursoAtual ? (
              <>
                Um botão por produto do curso. Ligado é o que o aluno com aquele
                produto vê; desligar tira a ferramenta da tela de quem pagou, e por
                isso pede o porquê.
              </>
            ) : (
              <>
                Cada ferramenta e os cursos que a entregam. Para ligar ou desligar,
                escolha um curso acima — o ato é do curso, não da ferramenta.
              </>
            )}
          </p>
        </div>

        {!cursoAtual ? (
          /* ── A visão de TODAS (22/09) ────────────────────────────────────
             Leitura, não edição: cada linha diz em quais cursos a ferramenta
             está. Os botões de ligar/desligar não vêm aqui de propósito — um
             por curso em cada linha daria onze por ferramenta, e o ato é do
             curso, então escolher um é parte de fazê-lo. */
          linhas.length === 0 ? (
            <p className="p-6 text-sm text-cinza">
              {busca.trim() ? "Nenhuma ferramenta com esse nome." : "Nenhuma ferramenta no catálogo."}
            </p>
          ) : (
            <ul className="divide-y divide-borda" data-todas-ferramentas={linhas.length}>
              {linhas.slice(0, mostrando).map(({ card, ligados }) => {
                // Os cursos DISTINTOS que entregam esta ferramenta. A Escola tem
                // dois SKUs com as mesmas ferramentas, e listar produto daria o
                // mesmo curso duas vezes.
                const cursos = [
                  ...new Map(
                    card.produtos
                      .map((x) => cursoDoProduto.get(x.slug))
                      .filter((s): s is string => Boolean(s))
                      .map((s) => [s, catalogo.cursos.find((c) => c.slug === s)?.nome ?? s]),
                  ),
                ];
                return (
                  <li key={card.slug} data-ferramenta={card.slug} className="flex flex-wrap items-start gap-4 p-5">
                    <div className="min-w-56 flex-1">
                      <p className="font-semibold text-titulo">{card.nome}</p>
                      {card.descricao && (
                        <p className="mt-0.5 text-sm text-cinza">{card.descricao}</p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {cursos.length === 0 ? (
                          /* Os DOIS ramos são ditos: sem este, a ausência de
                             chip se lê como "ainda não carregou". Ferramenta em
                             zero cursos não chega a aluno nenhum, e isso é o
                             fato mais importante da linha. */
                          <span className="rounded-full bg-alerta-fundo px-2 py-0.5 text-xs text-alerta-tinta">
                            em nenhum curso — nenhum aluno vê
                          </span>
                        ) : (
                          cursos.map(([slug, nome]) => (
                            <button
                              key={slug}
                              type="button"
                              data-ir-para-curso={slug}
                              onClick={() => escolherCurso(slug)}
                              title={`Abrir «${nome}» para ligar ou desligar`}
                              className="rounded-full bg-chip px-2 py-0.5 text-xs text-chip-tinta transition hover:bg-acento hover:text-acento-tinta"
                            >
                              {nome}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                    <span className="whitespace-nowrap text-sm text-cinza" data-em-cursos={ligados}>
                      {ligados === 1 ? "1 curso" : `${ligados} cursos`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )
        ) : linhas.length === 0 ? (
          <p className="p-6 text-sm text-cinza">
            {busca.trim()
              ? "Nenhum card com esse nome."
              : "Não há card que passe pelo par (produto, card)."}
          </p>
        ) : (
          <ul className="divide-y divide-borda">
            {linhas.slice(0, mostrando).map(({ card }) => {
              const secoesDaqui = card.secoes.filter((s) => s.curso_slug === curso);
              // UM botão por card, e ele age no CURSO — não um por SKU.
              //
              // A Escola é o único curso com dois produtos (Pro e Vitalícia), e
              // medido em 21/09 os dois mostram exatamente os mesmos 10 cards.
              // Um botão por SKU deixaria a Vitalícia para trás na primeira
              // ferramenta nova, em silêncio; quem garante que isso não
              // acontece é a RPC da 000269, que liga nos dois numa transação
              // só. Nos outros dez cursos não há diferença nenhuma.
              const chave = `${curso}:${card.slug}`;
              const total = produtosDoCurso.length;
              const quantos = card.produtos.filter((x) =>
                produtosDoCurso.some((p) => p.slug === x.slug),
              ).length;
              const ligado = total > 0 && quantos === total;
              // O estado PARCIAL não devia existir e o banco ainda pode
              // carregá-lo de antes desta tela. Ele é DITO em vez de
              // arredondado para ligado ou desligado — os dois arredondamentos
              // mentem, e é justamente o estado que alguém precisa consertar.
              const parcial = quantos > 0 && quantos < total;
              const alunosDoCurso = produtosDoCurso.reduce((s, p) => s + p.alunos, 0);
              return (
                <li
                  key={card.slug}
                  data-card-linha={card.slug}
                  className={`flex flex-wrap items-start gap-4 p-4 ${card.ativo ? "" : "bg-chip/40"}`}
                >
                  <div className="min-w-56 flex-1">
                    <p className="font-semibold text-titulo">
                      {card.nome}
                      {!card.ativo && (
                        <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-xs font-normal text-chip-tinta">
                          desativado
                        </span>
                      )}
                    </p>
                    {/* O tipo e o destino aparecem crus, com o nome que o banco
                        usa. Traduzi-los ("interno" → "mora no hub") criaria um
                        segundo vocabulário, e as recusas das RPCs — que citam
                        `interno`, `memberkit`, `sso-cfc` — deixariam de casar
                        com o que está escrito na tela. */}
                    <p className="mt-0.5 text-sm text-cinza">
                      {card.slug} · {card.tipo} · destino {card.destino}
                      {card.destino === "url" && card.url ? ` · ${card.url}` : ""}
                    </p>
                    {/* A linha da seção só vale para card LIGADO a algum produto
                        do curso. Num card que não está ligado, "fora de seção"
                        é verdade e mente: o estado que essa frase existe para
                        avisar — *ligado ao produto, fora de toda seção, some da
                        tela do aluno* — não é o dele, e num curso com 20 cards
                        desligados ela vira vinte avisos falsos que ensinam a
                        ignorar os verdadeiros. */}
                    {cursoAtual.secoes.length > 0 &&
                      produtosDoCurso.some((p) => card.produtos.some((x) => x.slug === p.slug)) && (
                        <p className="mt-0.5 text-sm text-cinza">
                          {secoesDaqui.length > 0
                            ? `na seção «${secoesDaqui.map((s) => s.titulo).join("», «")}»`
                            : "fora de seção"}
                        </p>
                      )}
                  </div>

                  <div className="flex flex-wrap items-start gap-2">
                    {(() => {
                      const confirmando =
                        confirmandoPar?.produto === curso && confirmandoPar.card === card.slug;
                      return (
                        <div
                          key={chave}
                          data-par={chave}
                          className={`flex flex-col gap-2 ${confirmando || falha?.onde === chave ? "w-full" : ""}`}
                        >
                          <button
                            type="button"
                            data-acao={ligado ? "desligar" : "ligar"}
                            data-parcial={parcial ? "sim" : undefined}
                            title={cursoAtual.nome}
                            aria-label={`${ligado ? "Desligar" : "Ligar"} «${card.nome}» ${ligado ? "de" : "em"} «${cursoAtual.nome}»`}
                            disabled={salvando}
                            onClick={() => {
                              setRecado(null);
                              setFalha(null);
                              // As DUAS direções abrem a confirmação (22/09).
                              // Antes, ligar agia no clique — e o argumento
                              // ("ligar não tira nada de ninguém") media só o
                              // lado da perda. Pôr uma ferramenta na tela de
                              // milhares de alunos também é ato de alcance, e
                              // desfazer não desfaz o que já foi visto.
                              setEdicao({
                                tipo: "ligar-desligar",
                                ligar: !ligado,
                                produto: curso,
                                card: card.slug,
                                motivo: "",
                              });
                            }}
                            className={`min-h-10 self-start rounded-full px-3 text-sm font-semibold transition disabled:opacity-55 ${
                              ligado
                                ? "bg-acento text-acento-tinta"
                                : parcial
                                  ? "border border-alerta-tinta/60 bg-alerta-fundo text-alerta-tinta"
                                  : "border border-borda text-cinza hover:bg-chip hover:text-tinta"
                            }`}
                          >
                            <span aria-hidden="true">{ligado ? "✓ " : "+ "}</span>
                            {parcial ? `Ligar nos ${total} (está em ${quantos})` : ligado ? "Ligado" : "Ligar"}
                          </button>

                          {confirmando && (
                            /*
                              A MESMA caixa serve às duas direções, e só o TOM
                              muda: desligar é da família `alerta` (laranja, a
                              de atenção desta casa), ligar é neutro. Cor igual
                              nos dois diria que as consequências são iguais, e
                              elas não são — uma tira da tela de quem já tem, a
                              outra acrescenta.
                            */
                            <div
                              data-confirmando={confirmandoPar.ligar ? "ligar" : "desligar"}
                              className={`rounded-cf border p-3 ${
                                confirmandoPar.ligar
                                  ? "border-borda bg-chip"
                                  : "border-alerta-tinta/40 bg-alerta-fundo"
                              }`}
                            >
                              <p
                                className={`text-sm ${confirmandoPar.ligar ? "text-tinta" : "text-alerta-tinta"}`}
                              >
                                {confirmandoPar.ligar ? (
                                  <>
                                    Ligar «{card.nome}» em «{cursoAtual.nome}» põe a ferramenta
                                    na tela de quem tem este curso — são {alunosDoCurso} aluno(s)
                                    com acesso vigente
                                    {total > 1 && `, somando os ${total} SKUs`}. Quantos passam a
                                    ver é o banco que conta, na resposta.
                                  </>
                                ) : (
                                  <>
                                    Desligar «{card.nome}» de «{cursoAtual.nome}» tira a
                                    ferramenta da tela de quem tem este curso — são{" "}
                                    {alunosDoCurso} aluno(s) com acesso vigente
                                    {total > 1 && `, somando os ${total} SKUs`}. Quantos
                                    deixam de ver a ferramenta é o banco que conta, na
                                    resposta.
                                  </>
                                )}
                              </p>
                              <label className="mt-3 flex flex-col gap-1.5">
                                <span
                                  className={`text-sm font-semibold ${
                                    confirmandoPar.ligar ? "text-tinta" : "text-alerta-tinta"
                                  }`}
                                >
                                  Justifique a alteração
                                </span>
                                <input
                                  data-campo="motivo"
                                  className={CAMPO}
                                  value={confirmandoPar.motivo}
                                  onChange={(e) =>
                                    setEdicao({ ...confirmandoPar, motivo: e.target.value })
                                  }
                                  placeholder="Vai para o razão de mudanças"
                                />
                              </label>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {/* Sem `disabled` por motivo vazio: quem decide
                                    se o porquê é obrigatório é o banco, e a
                                    recusa dele traz o número de alunos — que é
                                    justamente o que falta para escrever o
                                    motivo. Desabilitar aqui esconderia isso. */}
                                <button
                                  type="button"
                                  data-confirmar-vitrine
                                  disabled={salvando}
                                  onClick={() =>
                                    void agir(chave, () =>
                                      ligarCardNoCurso(
                                        curso,
                                        card.slug,
                                        confirmandoPar.ligar,
                                        confirmandoPar.motivo.trim() || null,
                                      ),
                                    )
                                  }
                                  className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                                >
                                  {salvando
                                    ? confirmandoPar.ligar
                                      ? "Ligando…"
                                      : "Desligando…"
                                    : confirmandoPar.ligar
                                      ? "Ligar"
                                      : "Desligar"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEdicao(null)}
                                  className="btn-contorno min-h-10 text-sm"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )}

                          {falha?.onde === chave && <Recado tipo="erro" texto={falha.texto} />}
                        </div>
                      );
                    })()}

                    {/* Um LINK, e não um botão: a tela dedicada tem endereço
                        próprio, e o operador precisa poder mandá-lo a um colega
                        e voltar pelo botão do navegador. */}
                    <a
                      href={`?curso=${encodeURIComponent(curso)}&card=${encodeURIComponent(card.slug)}`}
                      data-editar-card={card.slug}
                      className="btn-contorno inline-flex min-h-10 items-center self-start text-sm"
                    >
                      Abrir
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {linhas.length > mostrando && (
          <div className="border-t border-borda p-4 text-center">
            <button
              type="button"
              data-mostrar-mais
              onClick={() => setMostrando((n) => n + 25)}
              className="btn-contorno min-h-10 text-sm"
            >
              Mostrar mais ({linhas.length - mostrando} restantes)
            </button>
          </div>
        )}
      </section>

      {/* ── Os cards que não passam pelo par ───────────────────────────────── */}
      {cursoAtual && semPar.length > 0 && (
        <section
          className="surface rise p-5"
          role="group"
          aria-labelledby="titulo-sem-par"
        >
          <h3 id="titulo-sem-par" className="font-display text-base font-bold text-titulo">
            {semPar.length} card(s) sem par possível
          </h3>
          <p className="mt-1 text-sm text-cinza">
            Estes não se ligam a produto: o banco recusa, e não há botão aqui
            porque oferecê-lo seria prometer um ato que não existe.
          </p>
          <ul className="mt-3 divide-y divide-borda">
            {semPar.map((c) => (
              <li
                key={c.slug}
                data-card-sem-par={c.slug}
                className="flex flex-wrap items-center gap-3 py-2"
              >
                <span className="min-w-48 flex-1 text-sm font-semibold text-titulo">{c.nome}</span>
                <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-chip-tinta">
                  {c.universal ? "universal" : "escopo conta"}
                </span>
                <span className="text-sm text-cinza">
                  {c.universal
                    ? "aparece em todo curso, sem passar pelo par"
                    : "aparece uma vez na tela inicial, fora de qualquer curso"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── As seções ──────────────────────────────────────────────────────── */}
      {cursoAtual && (
        <>
          {formSecao && (
            <section
              ref={caixaDaSecao}
              role="group"
              aria-labelledby="titulo-form-secao"
              className="surface rise p-6"
            >
              <h3 id="titulo-form-secao" className="font-display text-lg font-bold text-titulo">
                {formSecao.id ? "Renomear seção" : "Nova seção"}
              </h3>

              {/* Criar a PRIMEIRA seção muda o desenho do curso inteiro: a tela
                  passa a desenhar só o que está dentro de seções, e todo card
                  que ficar de fora some. Dizer isso depois de criar seria tarde
                  — é a família do "Limite com a prorrogação", em que o que a
                  tela não diz o olho preenche com a suposição errada. */}
              {!formSecao.id && cursoAtual.secoes.length === 0 && (
                <p className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3 text-sm text-alerta-tinta">
                  Este curso ainda não tem seções, e por isso mostra todos os cards
                  ligados. Criando a primeira, a tela passa a desenhar só o que
                  está dentro de alguma seção — os {ligadosNoCurso}{" "}
                  card(s) ligados hoje somem até você pôr cada um numa.
                </p>
              )}

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Título</span>
                  <input
                    data-campo="titulo"
                    className={CAMPO}
                    value={formSecao.titulo}
                    onChange={(e) => setEdicao({ ...formSecao, titulo: e.target.value })}
                    placeholder="É o que o aluno lê acima do grupo de cards"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-semibold text-tinta">Ordem</span>
                  <input
                    data-campo="ordem-secao"
                    className={CAMPO}
                    value={formSecao.ordem}
                    inputMode="numeric"
                    onChange={(e) =>
                      setEdicao({
                        ...formSecao,
                        ordem: e.target.value.replace(/\D/g, "").slice(0, 4),
                      })
                    }
                    placeholder="em branco, vai para o fim"
                  />
                </label>

                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
                  <input
                    data-campo="motivo"
                    className={CAMPO}
                    value={formSecao.motivo}
                    onChange={(e) => setEdicao({ ...formSecao, motivo: e.target.value })}
                    placeholder="Vai para o razão de mudanças"
                  />
                </label>
              </div>

              {falha?.onde === "form-secao" && <Recado tipo="erro" texto={falha.texto} />}

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  data-confirmar-vitrine
                  disabled={salvando}
                  onClick={() =>
                    void agir("form-secao", () =>
                      salvarSecao({
                        curso_slug: cursoAtual.slug,
                        titulo: formSecao.titulo,
                        id: formSecao.id,
                        ordem: formSecao.ordem.trim() === "" ? null : Number(formSecao.ordem),
                        motivo: formSecao.motivo.trim() || null,
                      }),
                    )
                  }
                  className="pill-cta min-h-12 px-6 disabled:opacity-55"
                >
                  {salvando ? "Salvando…" : formSecao.id ? "Salvar" : "Criar seção"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEdicao(null);
                    setFalha(null);
                  }}
                  className="btn-contorno min-h-12 text-sm"
                >
                  Cancelar
                </button>
              </div>
            </section>
          )}

          <section className="surface rise overflow-hidden" role="group" aria-labelledby="titulo-secoes">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borda p-5">
              <div>
                <h3 id="titulo-secoes" className="font-display text-base font-bold text-titulo">
                  Seções de «{cursoAtual.nome || cursoAtual.slug}»
                </h3>
                <p className="mt-1 text-sm text-cinza">
                  A seção é do par (curso, card): o mesmo card está em «Aprendizado
                  e CFC» na Escola e estaria em outra numa pós.
                </p>
              </div>
              <button
                type="button"
                data-nova-secao
                onClick={() => {
                  setRecado(null);
                  setFalha(null);
                  setEdicao({ tipo: "secao", id: null, titulo: "", ordem: "", motivo: "" });
                }}
                className="btn-contorno min-h-10 text-sm"
              >
                + Nova seção
              </button>
            </div>

            {cursoAtual.secoes.length === 0 ? (
              /* Curso sem seção NÃO é erro, e a tela não pode parecer acusá-lo:
                 é o layout padrão (portas + ferramentas), e com quatro ou cinco
                 cards seccionar é burocracia. Nove dos onze cursos estão assim. */
              <p data-sem-secoes className="p-6 text-sm text-cinza">
                Este curso não tem seções, e usa o layout padrão: as portas em cima
                e as ferramentas embaixo, com todos os cards ligados aparecendo. É
                o certo para quem tem poucos cards — hoje só a Escola tem seções.
              </p>
            ) : (
              <ul className="divide-y divide-borda">
                {cursoAtual.secoes.map((s) => (
                  <li key={s.id} data-secao={s.id} className="p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-semibold text-titulo">
                        {s.titulo}{" "}
                        <span className="text-sm font-normal text-cinza">
                          ordem {s.ordem} · {s.cards.length} card(s)
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          data-renomear-secao={s.id}
                          onClick={() => {
                            setRecado(null);
                            setFalha(null);
                            setEdicao({
                              tipo: "secao",
                              id: s.id,
                              titulo: s.titulo,
                              ordem: String(s.ordem),
                              motivo: "",
                            });
                          }}
                          className="btn-contorno min-h-10 text-sm"
                        >
                          Renomear
                        </button>
                        <button
                          type="button"
                          data-remover-secao={s.id}
                          onClick={() => {
                            setRecado(null);
                            setFalha(null);
                            setEdicao({
                              tipo: "remover-secao",
                              id: s.id,
                              titulo: s.titulo,
                              motivo: "",
                            });
                          }}
                          className="btn-contorno min-h-10 text-sm"
                        >
                          Apagar
                        </button>
                      </div>
                    </div>

                    <ul className="mt-3 flex flex-wrap gap-2">
                      {s.cards.length === 0 && (
                        <li className="text-sm text-cinza">
                          Seção vazia — ela não desenha nada na tela do aluno.
                        </li>
                      )}
                      {s.cards.map((slug) => (
                        <li
                          key={slug}
                          data-secao-card={slug}
                          className="flex items-center gap-2 rounded-full bg-chip px-3 py-1 text-sm text-chip-tinta"
                        >
                          {porSlug.get(slug)?.nome ?? slug}
                          <button
                            type="button"
                            data-tirar-da-secao={slug}
                            disabled={salvando}
                            aria-label={`Tirar «${porSlug.get(slug)?.nome ?? slug}» da seção «${s.titulo}»`}
                            onClick={() =>
                              void agir(`secao:${s.id}`, () =>
                                porCardNaSecao(s.id, slug, false),
                              )
                            }
                            className="font-bold text-cinza transition hover:text-titulo disabled:opacity-55"
                          >
                            <span aria-hidden="true">×</span>
                          </button>
                        </li>
                      ))}
                    </ul>

                    {/* Só os órfãos entram no seletor: pôr um card que já está em
                        outra seção deste curso é recusado pelo gatilho
                        `tg_card_uma_secao_por_curso`, e oferecer o que sempre
                        recusa é botão que só serve para errar. */}
                    {orfaos.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-end gap-2">
                        <label className="flex min-w-56 flex-1 flex-col gap-1.5">
                          <span className="text-sm font-semibold text-tinta">
                            Pôr um card que está fora de seção
                          </span>
                          <select
                            data-por-card={s.id}
                            className={CAMPO}
                            value={aPor[s.id] ?? ""}
                            onChange={(e) => setAPor({ ...aPor, [s.id]: e.target.value })}
                          >
                            <option value="">Escolha um card…</option>
                            {orfaos.map((c) => (
                              <option key={c.slug} value={c.slug}>
                                {c.nome}
                              </option>
                            ))}
                          </select>
                        </label>
                        {/* Desligado PERMANENTE pede desenho próprio, e não
                            transparência. Este botão NASCE desligado — o
                            seletor ao lado começa vazio —, então é o estado em
                            que o operador olha para ele tentando entender, e
                            não um "Salvando…" de um segundo, que é para o que
                            `pill-cta disabled:opacity-*` serve. A medição está
                            no `index.css`: texto azul sobre laranja a 50% dá
                            2,73 : 1 no claro e 1,57 sobre navy, ou seja reprova
                            nos dois. A `pill-desligada` troca a opacidade por
                            borda tracejada e `cursor: not-allowed` — é a forma
                            que o botão "Criar turma" do Admin Pós já usa, no
                            mesmo arranjo de seletor + ação. */}
                        <button
                          type="button"
                          data-por-na-secao={s.id}
                          disabled={salvando || !aPor[s.id]}
                          onClick={() => {
                            const alvo = aPor[s.id];
                            if (!alvo) return;
                            void agir(`secao:${s.id}`, async () => {
                              const r = await porCardNaSecao(s.id, alvo, true);
                              // Atualização por FUNÇÃO: entre o clique e a
                              // resposta o operador pode ter escolhido um card
                              // em OUTRA seção — os seletores não ficam
                              // desligados —, e espalhar o `aPor` capturado no
                              // render apagaria essa escolha.
                              if (r.ok) setAPor((atual) => ({ ...atual, [s.id]: "" }));
                              return r;
                            });
                          }}
                          className={
                            aPor[s.id] && !salvando
                              ? "pill-cta min-h-12 px-5 text-sm"
                              : "pill-desligada min-h-12 px-5 text-sm"
                          }
                        >
                          Pôr nesta seção
                        </button>
                      </div>
                    )}

                    {removendo?.id === s.id && (
                      <div className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3">
                        <p className="text-sm text-alerta-tinta">
                          Apagar «{s.titulo}» não apaga card nenhum — mas os{" "}
                          {s.cards.length} que estão nela ficam fora de seção, e num
                          curso com seções isso os tira da tela do aluno.
                        </p>
                        <label className="mt-3 flex flex-col gap-1.5">
                          <span className="text-sm font-semibold text-alerta-tinta">Justifique a alteração</span>
                          <input
                            data-campo="motivo"
                            className={CAMPO}
                            value={removendo.motivo}
                            onChange={(e) => setEdicao({ ...removendo, motivo: e.target.value })}
                            placeholder="Vai para o razão de mudanças"
                          />
                        </label>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            data-confirmar-vitrine
                            disabled={salvando}
                            onClick={() =>
                              void agir(`secao:${s.id}`, () =>
                                removerSecao(s.id, removendo.motivo),
                              )
                            }
                            className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                          >
                            {salvando ? "Apagando…" : "Apagar seção"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEdicao(null)}
                            className="btn-contorno min-h-10 text-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {falha?.onde === `secao:${s.id}` && (
                      <Recado tipo="erro" texto={falha.texto} />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <p className="text-sm text-cinza">
        Card não se apaga por aqui, e produto novo não nasce aqui: as duas coisas
        vivem em mais de um lugar, e criar só o primeiro faz o aluno pagar e não
        ver o curso. O que esta tela faz é ligar, desligar e arrumar — e cada ato
        fica no razão, na sub-aba Mudanças, com o porquê que você escreveu.
      </p>
    </div>
  );
}
